"use client";

import { Briefcase, FileText, MessageCircle, Ticket, User, Users2 } from "lucide-react";
import { type ReactNode } from "react";
import type { ArticleDocument, ApplicationDocument, ClientDocument, FeedbackDocument, SupportTicketDocument, UserDocument, SearchResults } from "@/lib/search";
import { ARTICLE_TYPE_LABELS, PREVIEW_COUNT, type FlatItem } from "./search";

export function SearchResultsContent({
  results,
  flatItems,
  activeIndex,
  onSelect,
  hasAnyResults,
  loading,
  query,
}: {
  results: SearchResults;
  flatItems: FlatItem[];
  activeIndex: number;
  onSelect: (href: string) => void;
  hasAnyResults: boolean;
  loading: boolean;
  query: string;
}) {
  return (
    <>
      {!hasAnyResults && !loading && (
        <div className="px-4 py-6 text-center text-sm text-muted">
          No matches for &ldquo;{query.trim()}&rdquo;
        </div>
      )}

      <ResultGroup
        icon={<FileText size={14} />}
        label="Articles"
        hits={results.articles}
        flatItems={flatItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        renderTitle={(a: ArticleDocument) => a.processName}
        renderSubtitle={(a: ArticleDocument) => {
          const bits = [a.applicationName, a.clientName].filter(Boolean);
          const typeLabel = ARTICLE_TYPE_LABELS[a.type] ?? a.type;
          return [typeLabel, ...bits].join(" · ");
        }}
        hrefFor={(a: ArticleDocument) => `/articles/${a.id}`}
        keyFor={(a: ArticleDocument) => `article-${a.id}`}
        seeAllHref="/articles"
      />
      <ResultGroup
        icon={<Briefcase size={14} />}
        label="Applications"
        hits={results.applications}
        flatItems={flatItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        renderTitle={(a: ApplicationDocument) => a.name}
        renderSubtitle={(a: ApplicationDocument) => a.description}
        hrefFor={(a: ApplicationDocument) => `/applications/${a.id}`}
        keyFor={(a: ApplicationDocument) => `application-${a.id}`}
        seeAllHref="/applications"
      />
      <ResultGroup
        icon={<Users2 size={14} />}
        label="Clients"
        hits={results.clients}
        flatItems={flatItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        renderTitle={(c: ClientDocument) => c.name}
        renderSubtitle={(c: ClientDocument) => c.notes}
        hrefFor={(c: ClientDocument) => `/clients/${c.id}`}
        keyFor={(c: ClientDocument) => `client-${c.id}`}
        seeAllHref="/clients"
      />
      <ResultGroup
        icon={<Ticket size={14} />}
        label="Support Tickets"
        hits={results.supportTickets}
        flatItems={flatItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        renderTitle={(t: SupportTicketDocument) => t.title}
        renderSubtitle={(t: SupportTicketDocument) => t.creatorName}
        hrefFor={(t: SupportTicketDocument) => `/support-tickets/${t.id}`}
        keyFor={(t: SupportTicketDocument) => `ticket-${t.id}`}
        seeAllHref="/support-tickets"
      />
      <ResultGroup
        icon={<MessageCircle size={14} />}
        label="Feedback"
        hits={results.feedback}
        flatItems={flatItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        renderTitle={(f: FeedbackDocument) => f.message}
        renderSubtitle={(f: FeedbackDocument) => f.creatorName}
        hrefFor={() => "/feedback"}
        keyFor={(f: FeedbackDocument) => `feedback-${f.id}`}
        seeAllHref="/feedback"
      />
      <ResultGroup
        icon={<User size={14} />}
        label="Users"
        hits={results.users}
        flatItems={flatItems}
        activeIndex={activeIndex}
        onSelect={onSelect}
        renderTitle={(u: UserDocument) => u.name}
        renderSubtitle={(u: UserDocument) => u.roleName}
        hrefFor={(u: UserDocument) => `/users/${u.id}`}
        keyFor={(u: UserDocument) => `user-${u.id}`}
        seeAllHref="/admin/users"
      />

      {/* Full dedicated search page —
          grouped-by-field-match view the compact dropdown deliberately
          doesn't have room for. Shown whenever there's a real query,
          regardless of whether this dropdown found anything, since the
          full page's higher per-index limit can occasionally surface a
          match this preview's smaller one missed. */}
      {query.trim() && (
        <a
          href={`/search?q=${encodeURIComponent(query.trim())}`}
          className="block border-t border-line-subtle px-4 py-2.5 text-center text-xs font-medium text-brand-text hover:underline"
        >
          Open full search results for &ldquo;{query.trim()}&rdquo; →
        </a>
      )}
    </>
  );
}

function ResultGroup<T>({
  icon,
  label,
  hits,
  flatItems,
  activeIndex,
  onSelect,
  renderTitle,
  renderSubtitle,
  hrefFor,
  keyFor,
  seeAllHref,
}: {
  icon: ReactNode;
  label: "Articles" | "Applications" | "Clients" | "Support Tickets" | "Feedback" | "Users";
  hits: T[];
  flatItems: FlatItem[];
  activeIndex: number;
  onSelect: (href: string) => void;
  renderTitle: (hit: T) => string;
  renderSubtitle: (hit: T) => string | null;
  hrefFor: (hit: T) => string;
  keyFor: (hit: T) => string;
  seeAllHref: string;
}) {
  if (hits.length === 0) return null;
  const preview = hits.slice(0, PREVIEW_COUNT);

  return (
    <div className="border-b border-line-subtle py-2 last:border-b-0">
      <div className="flex items-center gap-1.5 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-subtle">
        {icon}
        {label}
      </div>
      <ul>
        {preview.map((hit) => {
          const href = hrefFor(hit);
          const key = keyFor(hit);
          const flatIndex = flatItems.findIndex((f) => f.key === key && f.group === label);
          const isActive = flatIndex !== -1 && flatIndex === activeIndex;
          const subtitle = renderSubtitle(hit);
          return (
            <li key={key}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(href)}
                className={`flex w-full flex-col items-start px-4 py-1.5 text-left ${
                  isActive
                    ? "bg-surface-hover"
                    : "hover:bg-surface-hover dark:hover:bg-neutral-800/60"
                }`}
              >
                <span className="truncate text-sm font-medium text-fg">
                  {renderTitle(hit)}
                </span>
                {subtitle && (
                  <span className="truncate text-xs text-muted">
                    {subtitle}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {hits.length > PREVIEW_COUNT && (
        <a
          href={seeAllHref}
          className="block px-4 py-1.5 text-xs font-medium text-muted hover:text-fg hover:underline "
        >
          See all {hits.length} in {label} →
        </a>
      )}
    </div>
  );
}
